import { Component, type ReactNode, type ErrorInfo } from 'react'
import { Banner, BlockStack, Button, Page, Text } from '@shopify/polaris'

type Props = {
  children: ReactNode
  fallbackTitle?: string
}

type State = {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  handleReset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    const { children, fallbackTitle = 'Something went wrong' } = this.props

    if (!error) return children

    return (
      <Page title={fallbackTitle}>
        <BlockStack gap="400">
          <Banner tone="critical">
            <BlockStack gap="200">
              <Text as="p" fontWeight="semibold">{error.message || 'An unexpected error occurred.'}</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                This module encountered an error. Other modules are unaffected.
              </Text>
            </BlockStack>
          </Banner>
          <Button onClick={this.handleReset}>Try again</Button>
        </BlockStack>
      </Page>
    )
  }
}
